import { useState, useRef, useEffect } from "react";
import { toast } from "../hooks/useToast";
import { doc, getDoc, updateDoc, deleteField, collection, getDocs, writeBatch } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage, auth } from "../firebase";
import {C} from "../constants/theme";
import { WebNavigationAppearance } from "./WebNavigation";
import "./toolWorkspace.css";
import "./settingsWorkspace.css";
import { ThemeAppearance } from "./ThemeAppearance";
import CheckoutBrick from "./CheckoutBrick";
import { fetchCheckoutPlano } from "../utils/checkoutPlano";
import PlanCards, { PLANOS_PAGOS } from "./PlanCards";
import { useAssinaturaPlay } from "../hooks/useAssinaturaPlay";
import { usePrecos } from "../hooks/usePrecos";
import { APP_VERSION } from "../App";
import { gerarSlug } from "../utils/slug";
import { espelharLojaPublica } from "../utils/lojaPublica";
import { limparWhatsapp, whatsappValido, limparInstagram, formatarWhatsapp } from "../utils/contatoLoja";
import { normalizarCelularBR, formatarTelefone } from "../utils/telefone";
import { versaoMaisNova } from "../utils/versaoApp";
import CuponsLoja from "./CuponsLoja";
import NotificacoesCelular from "./NotificacoesCelular";
import { TelaVendasOnline } from "./configuracoes/TelaVendasOnline";
import { TelaPagamentos } from "./configuracoes/pagamentos/TelaPagamentos";

const fns              = getFunctions(undefined, "us-central1");
const cfEnviarMsg      = httpsCallable(fns, "enviarMensagemSuporte");
const cfListarMsgs     = httpsCallable(fns, "listarMensagensSuporte");

const GERAR_URL   = "https://us-central1-matriz3dsite.cloudfunctions.net/gerarPreferencia";
const VALIDAR_URL = "https://us-central1-matriz3dsite.cloudfunctions.net/validarCupom";

const planoLabel = { pending:"Escolha seu plano", lifetime:"Acesso vitalício", trial:"Teste gratuito", monthly:"Mensal", annual:"Anual", expired:"Expirado", deleted:"Encerrado" };
const planoColor = { trial:C.warning, monthly:C.success, annual:C.accent2, expired:C.danger, deleted:C.danger };
const fmt = (d) => d?.toLocaleDateString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric" }) || "—";
// Preço vem do painel admin e pode ainda não ter chegado: sem valor, o recibo
// mostra "—" em vez de quebrar (.toFixed em null) ou inventar um número.
const valorDoPlano = (precos, plano) => {
  const valor = plano === "annual" ? precos.precoAnual : precos.precoMensal;
  return valor == null ? "—" : `R$ ${valor.toFixed(2)}${plano === "annual" ? "/ano" : "/mês"}`;
};

/* ── Estilos globais da tela (ripple, animações de entrada) ───── */
const PC_STYLES = `
.pc-ripple-host{ -webkit-tap-highlight-color: transparent; transition: transform .12s ease, border-color .15s; }
.pc-ripple-host:active{ transform: scale(0.97); }
.pc-ripple{ position:absolute; border-radius:50%; opacity:.16; transform:scale(0); animation: pc-rip .5s ease-out; pointer-events:none; }
@keyframes pc-rip{ to{ transform:scale(1); opacity:0; } }
.pc-card{ animation: pc-card-in .32s ease both; }
@keyframes pc-card-in{ from{ opacity:0; transform:translateY(8px);} to{ opacity:1; transform:translateY(0);} }
.pc-screen{ animation: pc-screen-in .22s cubic-bezier(.2,.8,.2,1) both; }
@keyframes pc-screen-in{ from{ opacity:0; transform:translateX(14px);} to{ opacity:1; transform:translateX(0);} }
`;

function useIsWide(breakpoint = 768) {
  const [wide, setWide] = useState(() => window.innerWidth >= breakpoint);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return wide;
}


/* ── Categorias (hub de configurações) ─────────────────────────
   Cada categoria concentra só as opções dela — telas curtas,
   fáceis de escanear com o polegar. */
const CATEGORIAS = [
  { id:"conta",         emoji:"🧑‍💻", color:"var(--pc-ui-00d4aa, #00d4aa)", label:"Conta",         desc:"Seu perfil, sua empresa e assinatura",
    itens:["perfil","nome da empresa","logo","nome do usuário","link da loja","plano atual","alterar plano","cupom de desconto","assinatura"] },
  { id:"pagamentos",    emoji:"💳", color:"var(--pc-ui-0ea5e9, #0ea5e9)", label:"Pagamentos",    desc:"Como você recebe dos clientes",
    itens:["pix","mercado pago","pagbank","infinitepay","gateway","trocar provedor","cartão","maquininha","taxa"] },
  { id:"vendas",        emoji:"🛒", color:"var(--pc-accent, #ff6b35)", label:"Vendas online", desc:"Custos e preferências de cada canal de venda",
    itens:["loja online","marketplace","shopee","mercado livre","amazon","embalagem","caixa","envio","custos adicionais","etiqueta","imposto","taxa do cartão","quem paga","padrão","lojas parceiras","consignação","consignado","revenda"] },
  { id:"loja",          emoji:"🏪", color:"#f97316", label:"Loja",          desc:"Contatos e cupons da sua loja pública",
    itens:["whatsapp","instagram","contato","redes sociais","link da loja","vitrine","site","cupom","cupons","desconto","promoção","validade"] },
  { id:"aparencia",     emoji:"🎨", color:"var(--pc-ui-38bdf8, #38bdf8)", label:"Aparência",     desc:"Tema e organização do menu",
    itens:["tema","ordem das abas","posição das abas","menu","horizontal","lateral","arrastar","travar layout","cor principal","idioma"] },
  { id:"backup",        emoji:"☁️", color:"#a78bfa", label:"Backup",        desc:"Baixe uma cópia ou restaure seus registros",
    itens:["exportar json","importar backup","backup automático"] },
  { id:"downloads",     emoji:"📥", color:"var(--pc-ui-22c55e, #22c55e)", label:"Downloads",     desc:"Acesse o Print Control nos seus dispositivos",
    itens:["windows","android","historico de versoes"] },
  { id:"sistema",       emoji:"🛠️", color:"var(--pc-ui-94a3b8, #94a3b8)", label:"Sistema",       desc:"Notificações, manutenção e informações do app",
    itens:["verificar atualizacoes","limpar cache","politica de privacidade","termos","versao"] },
  { id:"suporte",       emoji:"💬", color:"#ec4899", label:"Suporte",       desc:"Tire dúvidas, envie sugestões e reveja o tutorial",
    itens:["whatsapp","email","abrir chamado","historico de mensagens","tutorial","rever tutorial","ajuda","como usar"] },
];

/* ── Primitivas de navegação (ripple, busca, cards) ────────────── */
function CategoryCard({ cat, onClick, delay = 0 }) {
  return <button type="button" className="settings-category" onClick={onClick} style={{"--settings-color":cat.color,animationDelay:delay+"ms"}}><span className="settings-category-icon" aria-hidden="true">{cat.emoji}</span><span className="settings-category-copy"><strong>{cat.label}</strong><span>{cat.desc}</span></span><span className="settings-arrow" aria-hidden="true">↗</span></button>;
}

function CategoryRowCompact({ cat, ativo, onClick }) {
  return <button type="button" className={"settings-nav-item" + (ativo ? " is-active" : "")} aria-pressed={ativo} onClick={onClick} style={{"--settings-color":cat.color}}><span className="settings-category-icon" aria-hidden="true">{cat.emoji}</span><span><strong>{cat.label}</strong><small>{cat.desc}</small></span><span className="settings-arrow" aria-hidden="true">›</span></button>;
}

function DetailHeader({ cat, onBack }) {
  return <header className="settings-detail-header" style={{"--settings-color":cat.color}}>{onBack && <button type="button" className="settings-back" aria-label="Voltar para configurações" onClick={onBack}>←</button>}<span className="settings-category-icon" aria-hidden="true">{cat.emoji}</span><div><span className="tool-eyebrow">CONFIGURAÇÕES</span><h2>{cat.label}</h2><p>{cat.desc}</p></div></header>;
}

function EmBreveRow({ icon, title, subtitle = "Em breve" }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, background:"var(--pc-white-2, rgba(255,255,255,0.02))", border:"1px solid "+C.border, borderRadius:10, padding:"12px 14px", opacity:0.5 }}>
      <span style={{ fontSize:22 }}>{icon}</span>
      <div><div style={{ color:C.muted, fontSize:12, fontWeight:700 }}>{title}</div><div style={{ color:C.muted, fontSize:10 }}>{subtitle}</div></div>
    </div>
  );
}

function LinkRow({ icon, title, subtitle, href }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" style={{ display:"flex", alignItems:"center", gap:12, background:"var(--pc-white-2, rgba(255,255,255,0.02))", border:"1px solid "+C.border, borderRadius:10, padding:"12px 14px", textDecoration:"none" }}>
      <span style={{ fontSize:22 }}>{icon}</span>
      <div style={{ flex:1 }}><div style={{ color:C.text, fontSize:12, fontWeight:700 }}>{title}</div><div style={{ color:C.muted, fontSize:10 }}>{subtitle}</div></div>
      <span style={{ color:C.muted, fontSize:12 }}>↗</span>
    </a>
  );
}

/* ── Componentes de formulário reutilizados pelas telas ────────── */
const Section = ({ title, color = C.accent2, children, style }) => (
  <section className="settings-section" style={{ ...style, "--settings-color":color }}>
    <h3 className="settings-section-title">{title}</h3>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <label className="settings-field">
    <span>{label}</span>
    {children}
  </label>
);

const Inp = (props) => (
  <input {...props} style={{ width:"100%", padding:"10px 13px", background:"var(--pc-white-4, rgba(255,255,255,0.04))", border:"1px solid "+C.border, borderRadius:8, color:C.text, fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit", ...props.style }}/>
);

/* ── Tela: Conta (perfil + plano) ───────────────────────────────
   Dados da empresa/usuário e assinatura vivem juntos porque no
   fluxo do usuário são "quem eu sou + o que eu pago". */
function AbaPerfil({ perfil, uid, onUpdate }) {
  const [empresa,  setEmpresa]  = useState(perfil?.empresa || "");
  const [nome,     setNome]     = useState(perfil?.nome    || "");
  // WhatsApp do lojista: opcional e, por enquanto, sem código (a confirmação vem depois)
  const [telefone, setTelefone] = useState(perfil?.telefone ? formatarTelefone(perfil.telefone) : "");
  const [logoFile, setLogoFile] = useState(null);
  const [preview,  setPreview]  = useState(perfil?.logoUrl || "");
  const [salvando, setSalvando] = useState(false);
  const [ok,       setOk]       = useState(false);
  const [erro,     setErro]     = useState("");
  const fileRef = useRef();

  const escolherLogo = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return setErro("Selecione um arquivo de imagem.");
    setLogoFile(f); setPreview(URL.createObjectURL(f)); setErro("");
  };

  const salvar = async () => {
    if (!empresa.trim()) return setErro("Nome da empresa não pode ser vazio.");
    if (!nome.trim())    return setErro("Seu nome não pode ser vazio.");
    const numero = normalizarCelularBR(telefone);
    if (telefone.trim() && !numero) return setErro("Confira o WhatsApp: celular com DDD, por exemplo (73) 99999-0000.");
    setSalvando(true); setErro(""); setOk(false);
    try {
      let logoUrl = perfil?.logoUrl || "";
      if (logoFile) {
        const r = ref(storage, `users/${uid}/logo`);
        await uploadBytes(r, logoFile);
        logoUrl = await getDownloadURL(r);
      }
      await updateDoc(doc(db, "users", uid), { nome: nome.trim(), empresa: empresa.trim(), logoUrl, telefone: numero || deleteField() });
      // Atualiza o doc público da loja (perfil + PIX; sem segredos de pagamento)
      await espelharLojaPublica(uid, {
        empresa: empresa.trim(), nome: nome.trim(), logoUrl,
        email: perfil?.email, paymentConfig: perfil?.paymentConfig, contato: perfil?.contatoLoja,
      });
      if (auth.currentUser) await updateProfile(auth.currentUser, { displayName: nome.trim() });
      onUpdate(prev => ({ ...prev, nome: nome.trim(), empresa: empresa.trim(), logoUrl, telefone: numero || undefined }));
      if (numero) setTelefone(formatarTelefone(numero));
      setLogoFile(null); setOk(true);
      toast("Perfil atualizado!");
      setTimeout(() => setOk(false), 3000);
    } catch { setErro("Erro ao salvar. Tente novamente."); }
    setSalvando(false);
  };

  return (
    <div style={{ width:"100%" }}>
      <Section title="🏢 Perfil e empresa">
        <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:22, paddingBottom:20, borderBottom:"1px solid "+C.border }}>
          <div onClick={() => fileRef.current.click()} style={{ width:72, height:72, borderRadius:14, background:"rgba(0,212,170,0.06)", border:"1.5px dashed rgba(0,212,170,0.3)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", overflow:"hidden", flexShrink:0 }}>
            {preview ? <img src={preview} alt="logo" style={{ width:"100%", height:"100%", objectFit:"contain" }}/> : <span style={{ fontSize:24, opacity:.4 }}>🖼️</span>}
          </div>
          <div>
            <button onClick={() => fileRef.current.click()} style={{ background:"rgba(0,212,170,0.08)", border:"1px solid rgba(0,212,170,0.2)", color:C.accent2, borderRadius:8, padding:"7px 14px", fontSize:11, fontWeight:700, letterSpacing:1, cursor:"pointer" }}>
              {preview ? "TROCAR LOGO" : "ENVIAR LOGO"}
            </button>
            <div style={{ fontSize:10, color:C.muted, marginTop:5 }}>PNG, JPG ou SVG</div>
            {logoFile && <div style={{ fontSize:10, color:C.success, marginTop:3 }}>✓ {logoFile.name}</div>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={escolherLogo}/>
        </div>

        <Field label="Nome da empresa">
          <Inp value={empresa} onChange={e => setEmpresa(e.target.value)} placeholder="Minha Gráfica 3D"/>
        </Field>
        <Field label="Seu nome">
          <Inp value={nome} onChange={e => setNome(e.target.value)} placeholder="João Silva"/>
        </Field>
        <Field label="Seu WhatsApp (celular)">
          <Inp type="tel" inputMode="tel" autoComplete="tel" value={telefone} onChange={e => setTelefone(e.target.value)} placeholder="(73) 99999-0000"/>
        </Field>

        {/* Preview do link da loja (slug gerado do nome da empresa) */}
        <div style={{ background:"rgba(0,212,170,0.05)", border:"1px solid rgba(0,212,170,0.18)", borderRadius:8, padding:"10px 13px", marginBottom:16 }}>
          <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:C.muted, marginBottom:6, textTransform:"uppercase" }}>🔗 Link da sua loja</div>
          {gerarSlug(empresa) ? (
            <div style={{ fontSize:13, color:C.accent2, fontFamily:"monospace", wordBreak:"break-all" }}>
              {window.location.origin}/loja/<strong>{gerarSlug(empresa)}</strong>
            </div>
          ) : (
            <div style={{ fontSize:12, color:C.muted }}>Preencha o nome da empresa para gerar o link.</div>
          )}
        </div>

        {erro && <div style={{ background:"rgba(239,68,68,0.07)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:8, padding:"9px 12px", color:"#fca5a5", fontSize:12, marginBottom:12 }}>⚠️ {erro}</div>}
        {ok   && <div style={{ background:"rgba(46,213,115,0.07)", border:"1px solid rgba(46,213,115,0.2)", borderRadius:8, padding:"9px 12px", color:C.success, fontSize:12, marginBottom:12 }}>✓ Salvo com sucesso!</div>}
        <button onClick={salvar} disabled={salvando} style={{ width:"100%", padding:"11px", background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", borderRadius:9, color:"var(--pc-ui-04111f, #04111f)", fontSize:12, fontWeight:800, cursor:"pointer", letterSpacing:1, opacity:salvando?.6:1 }}>
          {salvando ? "⏳ Salvando..." : "💾 Salvar Alterações"}
        </button>
      </Section>
    </div>
  );
}

function AbaPlano({ perfil, uid, onUpdate }) {
  const [assinando,     setAssinando]     = useState(null);
  const [cancelando,    setCancelando]    = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [motivoCancel,  setMotivoCancel]  = useState("");
  const [motivoOutro,   setMotivoOutro]   = useState("");
  const MOTIVOS_CANCEL = ["Muito caro", "Não preciso mais", "Erros / bugs", "Não funciona direito", "Migrei pra outro sistema", "Outro"];
  const [preferenceId,  setPreferenceId]  = useState(null);
  const [checkoutId, setCheckoutId] = useState(null);
  const [planoBrick,    setPlanoBrick]    = useState(null);
  const [brickAmount,   setBrickAmount]   = useState(null);
  const [mostrarRecibo, setMostrarRecibo] = useState(false);
  const [couponInput,   setCouponInput]   = useState("");
  const [cupomOk,       setCupomOk]       = useState(null);
  const [erroCupom,     setErroCupom]     = useState("");
  const [validandoCupom, setValidandoCupom] = useState(false);
  const [erroPlano,     setErroPlano]     = useState("");
  const precos = usePrecos();
  // App Android: assinatura pela Google Play (a loja exige); no site e no PC, Mercado Pago
  const play = useAssinaturaPlay({ uid, onUpdate, setErro: setErroPlano });
  const daPlay = perfil?.planOrigem === "google_play";

  const trialEnds      = perfil?.trialEndsAt?.toDate?.() || (perfil?.trialEndsAt ? new Date(perfil.trialEndsAt) : null);
  const diasTrial      = trialEnds ? Math.max(0, Math.ceil((trialEnds - Date.now()) / 86400000)) : null;
  const planExpiry     = perfil?.planExpiresAt?.toDate?.() || null;
  const subscribedAt   = perfil?.subscribedAt?.toDate?.() || perfil?.updatedAt?.toDate?.() || null;
  const diasRestantes  = planExpiry ? Math.max(0, Math.ceil((planExpiry - Date.now()) / 86400000)) : null;
  const autoRenewCancelled = !!perfil?.autoRenewCancelled;
  const cancelamentoPendente = !!perfil?.cancelamentoPendente;
  const podeAssinar    = ["trial","expired","pending"].includes(perfil?.plan);

  const aplicarCupom = async (plano) => {
    const code = couponInput.trim(); if (!code) return;
    setValidandoCupom(true); setErroCupom(""); setCupomOk(null);
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetch(VALIDAR_URL, { method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` }, body:JSON.stringify({ code, plano: plano||"monthly" }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Cupom inválido");
      setCupomOk(d);
    } catch(e) { setErroCupom(e.message); }
    setValidandoCupom(false);
  };

  const assinar = async (plano) => {
    if (play.usarPlay) { await play.assinar(); return; }
    setAssinando(plano); setErroPlano("");
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetchCheckoutPlano(GERAR_URL, { method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` }, body:JSON.stringify({ plano, couponCode: cupomOk?.code || couponInput.trim() || null, origin: window.location.origin }) });
      if (!res.ok) { const d = await res.json().catch(()=>({})); throw new Error(d.error || "Erro ao iniciar checkout"); }
      const data = await res.json();
      setPlanoBrick(plano); setPreferenceId(data.preferenceId); setCheckoutId(data.checkoutId); setBrickAmount(data.amount || null);
    } catch(e) { setErroPlano(e.message || "Erro ao abrir checkout."); }
    setAssinando(null);
  };

  const cancelarAssinatura = async () => {
    const motivoFinal = motivoCancel === "Outro" ? motivoOutro.trim() : motivoCancel;
    if (!motivoFinal) { setErroPlano("Conte pra gente o motivo do cancelamento."); return; }
    setCancelando(true); setErroPlano("");
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetch("https://us-central1-matriz3dsite.cloudfunctions.net/cancelarAssinatura", { method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` }, body:JSON.stringify({ motivo: motivoFinal }) });
      if (!res.ok) { const d = await res.json().catch(()=>({})); throw new Error(d.error || "Erro ao cancelar"); }
      // Não muda o plano aqui — o pedido fica pendente até o admin aprovar
      // ou recusar (aba Cancelamentos do painel admin).
      onUpdate(prev => ({ ...prev, cancelamentoPendente: true }));
      setConfirmCancel(false); setMotivoCancel(""); setMotivoOutro("");
    } catch(e) { setErroPlano(e.message || "Erro ao cancelar."); }
    setCancelando(false);
  };

  const handleSuccess = (plano) => {
    const expiresAt = new Date(Date.now() + (plano==="annual" ? 365 : 30) * 86400000);
    onUpdate(prev => ({ ...prev, plan:plano, planExpiresAt:expiresAt, subscribedAt:new Date() }));
    setPreferenceId(null);
  };

  const cor = planoColor[perfil?.plan] || C.muted;

  return (
    <>
    {preferenceId && (
      <CheckoutBrick preferenceId={preferenceId} checkoutId={checkoutId} plano={planoBrick} uid={uid} onSuccess={handleSuccess}
        onClose={() => { setPreferenceId(null); setPlanoBrick(null); setBrickAmount(null); }}
        amount={brickAmount} couponCode={cupomOk?.code || null} />
    )}
    <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:14 }}>

      <Section title="Sua assinatura" color={cor}>
        <div className="settings-plan-current" style={{"--settings-color":cor}}>
          <div className="settings-plan-top"><div><span className="tool-eyebrow">PLANO ATUAL</span><h3>{planoLabel[perfil?.plan] || "Plano não informado"}</h3><p>{perfil?.plan === "trial" ? "Explore os recursos e organize sua produção." : PLANOS_PAGOS.includes(perfil?.plan) ? "Sua produção, seus clientes e sua loja em um só lugar." : perfil?.plan === "lifetime" ? "Seu acesso ao Print Control é vitalício." : "Encontre o plano para continuar com o Print Control."}</p></div><span className="settings-plan-symbol" aria-hidden="true">{perfil?.plan === "trial" ? "🎁" : "✦"}</span></div>
          {autoRenewCancelled && <div className="settings-plan-notice">Renovação cancelada · acesso até {fmt(planExpiry)}</div>}
          <dl className="settings-plan-facts">
            {PLANOS_PAGOS.includes(perfil?.plan) && <>{subscribedAt && <div><dt>Assinatura desde</dt><dd>{fmt(subscribedAt)}</dd></div>}{planExpiry && <div><dt>Válido até</dt><dd>{fmt(planExpiry)}</dd></div>}{diasRestantes !== null && <div><dt>Tempo restante</dt><dd>{diasRestantes} dia{diasRestantes !== 1 ? "s" : ""}</dd></div>}</>}
            {perfil?.plan === "trial" && <>{diasTrial !== null && <div><dt>Tempo de teste</dt><dd>{diasTrial > 0 ? diasTrial + " dia" + (diasTrial !== 1 ? "s" : "") : "Expirado"}</dd></div>}{trialEnds && <div><dt>Disponível até</dt><dd>{fmt(trialEnds)}</dd></div>}</>}
          </dl>
        </div>

        {/* Cupom */}
        {!play.usarPlay && !daPlay && (podeAssinar || PLANOS_PAGOS.includes(perfil?.plan)) && (
          <div style={{ background:"rgba(0,212,170,0.03)", border:"1px solid rgba(0,212,170,0.1)", borderRadius:9, padding:"10px 12px", marginBottom:14 }}>
            <div style={{ fontSize:9, fontWeight:700, color:C.muted, marginBottom:6, letterSpacing:1 }}>CUPOM DE DESCONTO</div>
            <div style={{ display:"flex", gap:7 }}>
              <input aria-label="Código do cupom" value={couponInput} onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCupomOk(null); setErroCupom(""); }} placeholder="Código do cupom"
                style={{ flex:1, padding:"6px 10px", background:"var(--pc-white-4, rgba(255,255,255,0.04))", border:"1px solid "+C.border, borderRadius:7, color:C.text, fontSize:11, fontFamily:"monospace", letterSpacing:1.5, outline:"none" }} />
              <button onClick={() => aplicarCupom(PLANOS_PAGOS.includes(perfil?.plan) ? perfil.plan : "monthly")} disabled={validandoCupom || !couponInput.trim()}
                style={{ padding:"6px 12px", background:"rgba(0,212,170,0.1)", border:"1px solid rgba(0,212,170,0.25)", color:C.accent2, borderRadius:7, fontSize:10, fontWeight:700, cursor:"pointer" }}>
                {validandoCupom ? "⏳" : "Aplicar"}
              </button>
            </div>
            {cupomOk  && <div style={{ marginTop:5, color:C.success, fontSize:10, fontWeight:700 }}>✓ -R$ {cupomOk.discount?.toFixed(2)} · Total: R$ {cupomOk.finalAmount?.toFixed(2)}</div>}
            {erroCupom && <div style={{ marginTop:5, color:"#fca5a5", fontSize:10 }}>⚠️ {erroCupom}</div>}
          </div>
        )}

        {/* Cards de assinatura */}
        {podeAssinar && (
          <div style={{ marginBottom:10 }}>
            <PlanCards precos={precos} precoTexto={play.usarPlay ? play.precoPlay : null} jaUsouTrial={true} assinando={assinando || (play.ocupado === "assinar" ? "monthly" : null)} onAssinar={assinar} compact showFeatures />
            {play.usarPlay && (
              <button type="button" onClick={play.restaurar} disabled={!!play.ocupado} style={{ marginTop:8, width:"100%", minHeight:40, background:"none", border:"none", color:C.muted, fontSize:12, textDecoration:"underline", cursor:"pointer" }}>
                {play.ocupado === "restaurar" ? "Conferindo…" : "Já assinei pela Google Play"}
              </button>
            )}
          </div>
        )}

        {/* Assinatura pela Google Play: renova e cancela pela Play Store */}
        {daPlay && PLANOS_PAGOS.includes(perfil?.plan) && (
          <div style={{ background:"rgba(0,212,170,0.04)", border:"1px solid rgba(0,212,170,0.12)", borderRadius:10, padding:"12px 14px", marginBottom:8, fontSize:12, color:C.muted, lineHeight:1.6 }}>
            Assinatura pela <strong style={{ color:C.text }}>Google Play</strong>: {autoRenewCancelled ? "a renovação está desligada." : "renova sozinha todo mês."} Para cancelar ou trocar a forma de pagamento, use a Play Store.
            <div style={{ marginTop:8 }}><a href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noreferrer" style={{ color:C.accent2, fontWeight:700 }}>Gerenciar na Google Play →</a></div>
          </div>
        )}

        {/* Renovar */}
        {!daPlay && PLANOS_PAGOS.includes(perfil?.plan) && !autoRenewCancelled && (
          <button onClick={() => assinar(perfil.plan)} disabled={!!assinando} style={{ width:"100%", padding:"10px", background:"rgba(0,212,170,0.07)", border:"1px solid rgba(0,212,170,0.25)", color:C.accent2, borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", marginBottom:8 }}>
            🔄 {assinando ? "⏳ Carregando..." : "Renovar assinatura"}
          </button>
        )}

        {/* Recibo */}
        {!daPlay && PLANOS_PAGOS.includes(perfil?.plan) && subscribedAt && (
          <button onClick={() => setMostrarRecibo(true)} style={{ width:"100%", padding:"10px", background:"rgba(14,165,233,0.07)", border:"1px solid rgba(14,165,233,0.25)", color:"var(--pc-ui-38bdf8, #38bdf8)", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", marginBottom:8 }}>
            🧾 Ver recibo / Imprimir
          </button>
        )}

        {/* Cancelar (Mercado Pago; a da Google Play é na Play Store) */}
        {!daPlay && PLANOS_PAGOS.includes(perfil?.plan) && (
          <div style={{ marginTop:4 }}>
            {cancelamentoPendente ? (
              <div style={{ background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:9, padding:"12px 14px", textAlign:"center" }}>
                <span style={{ color:C.warning, fontSize:11, fontWeight:700 }}>⏳ Pedido de cancelamento em análise — você recebe um e-mail assim que decidirmos.</span>
              </div>
            ) : !confirmCancel ? (
              <button onClick={() => setConfirmCancel(true)} style={{ background:"none", border:"1px solid rgba(239,68,68,0.25)", color:"#ef4444", borderRadius:7, padding:"7px 14px", fontSize:11, fontWeight:700, cursor:"pointer", width:"100%" }}>
                Cancelar assinatura
              </button>
            ) : (
              <div style={{ background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:9, padding:"14px" }}>
                <p style={{ color:"#fca5a5", fontSize:12, margin:"0 0 10px" }}>Seu pedido vai para análise — o acesso continua até decidirmos. Qual o motivo?</p>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                  {MOTIVOS_CANCEL.map(m => (
                    <button key={m} onClick={() => setMotivoCancel(m)}
                      style={{
                        padding:"6px 12px", borderRadius:20, fontSize:11, fontWeight:700, cursor:"pointer",
                        background: motivoCancel===m ? "rgba(239,68,68,0.18)" : "var(--pc-white-4, rgba(255,255,255,0.04))",
                        border: "1px solid " + (motivoCancel===m ? "rgba(239,68,68,0.5)" : C.border),
                        color: motivoCancel===m ? "#fca5a5" : C.muted,
                      }}>
                      {m}
                    </button>
                  ))}
                </div>
                {motivoCancel === "Outro" && (
                  <textarea aria-label="Motivo do cancelamento" value={motivoOutro} onChange={e=>setMotivoOutro(e.target.value)} rows={3}
                    style={{ width:"100%", background:"var(--pc-white-4, rgba(255,255,255,0.04))", border:"1px solid "+C.border, borderRadius:7, color:C.text, padding:"8px 10px", fontSize:12, resize:"vertical", outline:"none", fontFamily:"inherit", boxSizing:"border-box", marginBottom:10 }} />
                )}
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={cancelarAssinatura} disabled={cancelando || !motivoCancel || (motivoCancel==="Outro" && !motivoOutro.trim())} style={{ flex:1, background:"rgba(239,68,68,0.15)", border:"1px solid rgba(239,68,68,0.4)", color:"#ef4444", borderRadius:7, padding:"8px", fontSize:11, fontWeight:800, cursor:"pointer", opacity:(!motivoCancel || (motivoCancel==="Outro" && !motivoOutro.trim()))?.6:1 }}>{cancelando?"⏳":"Enviar pedido de cancelamento"}</button>
                  <button onClick={() => { setConfirmCancel(false); setMotivoCancel(""); setMotivoOutro(""); }} style={{ flex:1, background:"var(--pc-white-4, rgba(255,255,255,0.04))", border:"1px solid "+C.border, color:C.muted, borderRadius:7, padding:"8px", fontSize:11, cursor:"pointer" }}>Voltar</button>
                </div>
              </div>
            )}
          </div>
        )}

        {erroPlano && <div style={{ background:"rgba(239,68,68,0.07)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:8, padding:"8px 12px", color:"#fca5a5", fontSize:11, marginTop:10 }}>⚠️ {erroPlano}</div>}
      </Section>
    </div>

    {/* Modal recibo */}
    {mostrarRecibo && (
      <>
        <style>{`@media print { body > * { display:none !important; } .recibo-print { display:block !important; position:static !important; background:white !important; } }`}</style>
        <div className="recibo-print" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10000, padding:16 }}>
          <div style={{ background:"#fff", borderRadius:16, padding:"32px 28px", maxWidth:420, width:"100%", position:"relative" }}>
            <button onClick={() => setMostrarRecibo(false)} style={{ position:"absolute", top:12, right:14, background:"none", border:"none", fontSize:20, cursor:"pointer", color:"var(--pc-ui-94a3b8, #94a3b8)" }}>✕</button>
            <div style={{ textAlign:"center", marginBottom:24 }}>
              <div style={{ fontWeight:900, fontSize:18, letterSpacing:3, color:"var(--pc-ui-0a1628, #0a1628)" }}>PRINT <span style={{ color:"var(--pc-ui-00d4aa, #00d4aa)" }}>CONTROL</span></div>
              <div style={{ color:"#64748b", fontSize:11, letterSpacing:2, marginTop:2 }}>RECIBO DE ASSINATURA</div>
            </div>
            <div style={{ borderTop:"2px dashed #e2e8f0", borderBottom:"2px dashed #e2e8f0", padding:"16px 0", marginBottom:20 }}>
              {[["Cliente",perfil?.nome||"—"],["Empresa",perfil?.empresa||"—"],["E-mail",perfil?.email||auth.currentUser?.email||"—"],["Plano",`Print Control ${planoLabel[perfil?.plan]||"—"}`],["Valor",valorDoPlano(precos, perfil?.plan)],["Data início",fmt(subscribedAt)],["Válido até",fmt(planExpiry)]].map(([k,v])=>(
                <div key={k} style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}><span style={{ color:"#64748b", fontSize:12 }}>{k}</span><span style={{ color:"var(--pc-ui-0a1628, #0a1628)", fontSize:12, fontWeight:700 }}>{v}</span></div>
              ))}
            </div>
            <div style={{ textAlign:"center", marginBottom:20 }}>
              <div style={{ display:"inline-block", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"6px 20px" }}>
                <span style={{ color:"#15803d", fontSize:12, fontWeight:800 }}>✓ Assinatura ativa</span>
              </div>
            </div>
            <button onClick={() => window.print()} style={{ width:"100%", padding:"11px", background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", color:"var(--pc-ui-04111f, #04111f)", borderRadius:10, fontSize:13, fontWeight:800, cursor:"pointer" }}>🖨️ Imprimir recibo</button>
          </div>
        </div>
      </>
    )}
    </>
  );
}

function TelaConta({ perfil, uid, onUpdate, focarPlano }) {
  const [secao, setSecao] = useState(focarPlano ? "plano" : "perfil");
  useEffect(() => { if (focarPlano) setSecao("plano"); }, [focarPlano]);
  return <div className="settings-account"><div className="settings-segments" role="group" aria-label="Seções da conta"><button type="button" aria-pressed={secao === "perfil"} onClick={() => setSecao("perfil")}><span aria-hidden="true">🧑‍💻</span> Perfil e empresa</button><button type="button" aria-pressed={secao === "plano"} onClick={() => setSecao("plano")}><span aria-hidden="true">✦</span> Assinatura</button></div><div hidden={secao !== "perfil"}><AbaPerfil perfil={perfil} uid={uid} onUpdate={onUpdate} /></div><div hidden={secao !== "plano"} className="settings-subscription"><AbaPlano perfil={perfil} uid={uid} onUpdate={onUpdate} /></div></div>;
}

/* ── Tela: Suporte ───────────────────────────────────────────── */
function TelaSuporte({ perfil, onIniciarTutorial }) {
  const [mensagem,      setMensagem]      = useState("");
  const [enviando,      setEnviando]      = useState(false);
  const [enviouOk,      setEnviouOk]      = useState(false);
  const [erroEnvio,     setErroEnvio]     = useState("");
  const [msgs,          setMsgs]          = useState([]);
  const [carregandoMsgs, setCarregandoMsgs] = useState(true);

  useEffect(() => {
    cfListarMsgs().then(r => {
      const sorted = (r.data.mensagens || []).sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
      setMsgs(sorted);
    }).catch(() => {}).finally(() => setCarregandoMsgs(false));
  }, []);

  const enviar = async () => {
    if (!mensagem.trim()) return;
    setEnviando(true); setErroEnvio("");
    try {
      const r = await cfEnviarMsg({ mensagem: mensagem.trim() });
      const novaMsg = {
        id: r.data.id,
        mensagem: mensagem.trim(),
        createdAt: new Date().toISOString(),
        respondida: false,
        resposta: null,
        nome: perfil?.nome || "",
        empresa: perfil?.empresa || "",
      };
      setMsgs(prev => [novaMsg, ...prev]);
      setMensagem("");
      setEnviouOk(true);
      toast("Mensagem enviada!");
      setTimeout(() => setEnviouOk(false), 4000);
    } catch(e) { setErroEnvio(e.message || "Erro ao enviar. Tente novamente."); }
    setEnviando(false);
  };

  const fmtData = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" });
  };

  return (
    <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:16 }}>

      {/* Tutorial guiado */}
      {onIniciarTutorial && (
        <Section title="🎓 Conheça os recursos" color="var(--pc-ui-00d4aa, #00d4aa)">
          <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
            <div style={{ flex:1, minWidth:200, color:C.muted, fontSize:12, lineHeight:1.6 }}>
              Aprenda o essencial: cadastre filamentos e impressoras, calcule o preço e crie seu primeiro produto.
            </div>
            <button onClick={onIniciarTutorial}
              style={{ background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", borderRadius:10, color:"var(--pc-ui-04111f, #04111f)", fontSize:12.5, fontWeight:800, padding:"10px 18px", cursor:"pointer", letterSpacing:.3, whiteSpace:"nowrap" }}>
              ▶️ Rever tutorial
            </button>
          </div>
        </Section>
      )}

      {/* Contatos rápidos */}
      <Section title="📞 Fale com a equipe" color={C.accent2}>
        <div className="settings-contact-grid">
          <a href="mailto:printcontrol@gmail.com" style={{ flex:1, minWidth:200, display:"flex", alignItems:"center", gap:10, background:"rgba(0,212,170,0.05)", border:"1px solid rgba(0,212,170,0.2)", borderRadius:10, padding:"12px 16px", textDecoration:"none" }}
            onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(0,212,170,0.4)"} onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(0,212,170,0.2)"}>
            <span style={{ fontSize:20 }}>✉️</span>
            <div><div style={{ color:C.accent2, fontSize:12, fontWeight:700 }}>E-mail</div><div style={{ color:C.muted, fontSize:11 }}>printcontrol@gmail.com</div></div>
          </a>
          <a href="https://wa.me/5573999073081" target="_blank" rel="noreferrer" style={{ flex:1, minWidth:200, display:"flex", alignItems:"center", gap:10, background:"rgba(37,211,102,0.05)", border:"1px solid rgba(37,211,102,0.2)", borderRadius:10, padding:"12px 16px", textDecoration:"none" }}
            onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(37,211,102,0.4)"} onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(37,211,102,0.2)"}>
            <span style={{ fontSize:20 }}>💬</span>
            <div><div style={{ color:"#25d366", fontSize:12, fontWeight:700 }}>WhatsApp</div><div style={{ color:C.muted, fontSize:11 }}>(73) 99907-3081</div></div>
          </a>
        </div>
      </Section>

      {/* Enviar mensagem */}
      <Section title="✉️ Envie uma mensagem" color="#a78bfa">
        <div style={{ color:C.muted, fontSize:12, marginBottom:14, lineHeight:1.6 }}>
          Conte sua dúvida, relate um problema ou sugira uma melhoria. A resposta fica no histórico abaixo e também chega por e-mail.
        </div>
        <textarea
          aria-label="Mensagem para o suporte"
          value={mensagem}
          onChange={e => setMensagem(e.target.value)}
          placeholder="Como podemos ajudar?"
          rows={4}
          style={{ width:"100%", background:"var(--pc-white-4, rgba(255,255,255,0.04))", border:"1px solid "+C.border, borderRadius:10, color:C.text, padding:"12px 14px", fontSize:13, resize:"vertical", outline:"none", fontFamily:"inherit", boxSizing:"border-box", marginBottom:10 }}
        />
        {erroEnvio && <div style={{ background:"rgba(239,68,68,0.07)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:8, padding:"8px 12px", color:"#fca5a5", fontSize:12, marginBottom:10 }}>⚠️ {erroEnvio}</div>}
        {enviouOk  && <div style={{ background:"rgba(46,213,115,0.07)", border:"1px solid rgba(46,213,115,0.2)", borderRadius:8, padding:"8px 12px", color:C.success, fontSize:12, marginBottom:10 }}>✓ Mensagem enviada! Responderemos em breve.</div>}
        <button onClick={enviar} disabled={enviando || !mensagem.trim()}
          style={{ width:"100%", padding:"11px", background:mensagem.trim()?"linear-gradient(135deg,#a78bfa,#7c3aed)":"var(--pc-white-4, rgba(255,255,255,0.04))", border:"none", borderRadius:9, color:mensagem.trim()?"#fff":C.muted, fontSize:12, fontWeight:800, cursor:mensagem.trim()?"pointer":"not-allowed", letterSpacing:.5 }}>
          {enviando ? "⏳ Enviando..." : "📤 Enviar mensagem"}
        </button>
      </Section>

      {/* Histórico */}
      <Section title="📬 Histórico de mensagens" color={C.muted}>
        {carregandoMsgs ? (
          <div style={{ textAlign:"center", padding:24, color:C.muted, fontSize:13 }}>⏳ Carregando...</div>
        ) : msgs.length === 0 ? (
          <div className="settings-empty">Suas mensagens e as respostas da equipe aparecerão aqui.</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {msgs.map(m => (
              <div key={m.id} style={{ background:C.bg, border:"1px solid "+(m.origem==="admin"?"rgba(0,212,170,0.25)":C.border), borderRadius:12, padding:"14px 16px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:11, color:C.muted }}>{fmtData(m.createdAt)}</span>
                  {m.origem==="admin" ? (
                    <span style={{ background:"rgba(0,212,170,0.1)", border:"1px solid rgba(0,212,170,0.3)", color:C.accent2, borderRadius:6, padding:"2px 8px", fontSize:10, fontWeight:700 }}>📩 Suporte entrou em contato</span>
                  ) : (
                    <span style={{ background:m.respondida?"rgba(46,213,115,0.1)":"rgba(245,158,11,0.1)", border:"1px solid "+(m.respondida?"rgba(46,213,115,0.3)":"rgba(245,158,11,0.3)"), color:m.respondida?C.success:C.warning, borderRadius:6, padding:"2px 8px", fontSize:10, fontWeight:700 }}>
                      {m.respondida ? "✓ Respondida" : "⏳ Aguardando"}
                    </span>
                  )}
                </div>
                {m.mensagem && <div style={{ fontSize:13, color:C.text, lineHeight:1.6, marginBottom: m.resposta ? 12 : 0 }}>{m.mensagem}</div>}
                {m.resposta && (
                  <div style={{ background:"rgba(0,212,170,0.05)", border:"1px solid rgba(0,212,170,0.2)", borderRadius:8, padding:"10px 14px", marginTop:4 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:C.accent2, letterSpacing:1, marginBottom:6 }}>{m.mensagem ? "RESPOSTA DO SUPORTE" : "MENSAGEM DO SUPORTE"}</div>
                    <div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>{m.resposta}</div>
                    {m.respondidaAt && <div style={{ fontSize:10, color:C.muted, marginTop:6 }}>{fmtData(m.respondidaAt)}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function SalvarBloco({ salvando, ok, erro, onClick, label = "💾 Salvar configurações" }) {
  return (
    <>
      {erro && <div style={{ background:"rgba(239,68,68,0.07)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:8, padding:"9px 12px", color:"#fca5a5", fontSize:12 }}>⚠️ {erro}</div>}
      {ok   && <div style={{ background:"rgba(46,213,115,0.07)", border:"1px solid rgba(46,213,115,0.2)", borderRadius:8, padding:"9px 12px", color:C.success, fontSize:12 }}>✓ Salvo com sucesso!</div>}
      <button onClick={onClick} disabled={salvando}
        style={{ width:"100%", padding:11, background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", borderRadius:9, color:"var(--pc-ui-04111f, #04111f)", fontSize:12, fontWeight:800, cursor:"pointer", opacity:salvando?.6:1 }}>
        {salvando ? "⏳ Salvando..." : label}
      </button>
    </>
  );
}

function Carregando() {
  return <div style={{ color:C.muted, padding:40, textAlign:"center" }}>⏳ Carregando...</div>;
}

/* ── Tela: Loja (contato público da vitrine) ─────────────────────
   WhatsApp e Instagram digitados aqui vão pro doc público da loja
   (lojas/{slug}) e aparecem na vitrine e no rodapé do site. */
function TelaLoja({ uid, onUpdate }) {
  const [contato,  setContato]  = useState({ whatsapp: "", instagram: "" });
  const [loja,     setLoja]     = useState({});
  const [loading,  setLoading]  = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [ok,       setOk]       = useState(false);
  const [erro,     setErro]     = useState("");

  useEffect(() => {
    getDoc(doc(db, "users", uid))
      .then(snap => {
        if (!snap.exists()) return;
        const d = snap.data();
        setContato({
          whatsapp: formatarWhatsapp(d.contatoLoja?.whatsapp),
          instagram: d.contatoLoja?.instagram ? "@" + d.contatoLoja.instagram : "",
        });
        setLoja({ empresa: d.empresa, nome: d.nome, logoUrl: d.logoUrl, email: d.email });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [uid]);

  const salvar = async () => {
    const whatsapp = limparWhatsapp(contato.whatsapp);
    const instagram = limparInstagram(contato.instagram);
    if (contato.whatsapp.trim() && !whatsappValido(whatsapp)) return setErro("WhatsApp inválido — informe o DDD e o número.");
    if (contato.instagram.trim() && !instagram) return setErro("Instagram inválido — use só o @usuário ou o link do perfil.");
    setSalvando(true); setErro(""); setOk(false);
    try {
      const salvo = { whatsapp: whatsappValido(whatsapp) ? whatsapp : "", instagram };
      await updateDoc(doc(db, "users", uid), { contatoLoja: salvo });
      await espelharLojaPublica(uid, { ...loja, contato: salvo });
      onUpdate?.(prev => ({ ...prev, contatoLoja: salvo }));
      setContato({ whatsapp: formatarWhatsapp(salvo.whatsapp), instagram: salvo.instagram ? "@" + salvo.instagram : "" });
      setOk(true); toast("Contato da loja salvo!"); setTimeout(() => setOk(false), 3000);
    } catch { setErro("Erro ao salvar. Tente novamente."); }
    setSalvando(false);
  };

  if (loading) return <Carregando />;
  const slug = gerarSlug(loja.empresa || "");

  return (
    <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:16 }}>
      <Section title="📞 Contato da loja" color="#f97316">
        <div style={{ fontSize:12, color:C.muted, lineHeight:1.7, marginBottom:16 }}>
          Aparece na vitrine da sua loja online para o cliente falar com você. Deixe em branco o que não quiser mostrar.
        </div>
        <Field label="WhatsApp">
          <Inp type="tel" inputMode="tel" value={contato.whatsapp} onChange={e => setContato(c => ({ ...c, whatsapp: e.target.value }))} placeholder="(73) 99999-9999" />
        </Field>
        <Field label="Instagram">
          <Inp value={contato.instagram} autoCapitalize="none" onChange={e => setContato(c => ({ ...c, instagram: e.target.value }))} placeholder="@sualoja ou link do perfil" />
        </Field>
        {slug ? (
          <div style={{ fontSize:11, color:C.muted }}>
            🔗 Sua loja: {window.location.origin}/loja/<strong style={{ color:C.text }}>{slug}</strong>
          </div>
        ) : (
          <div style={{ background:"rgba(245,158,11,0.07)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:8, padding:"9px 12px", color:"#fcd34d", fontSize:12, lineHeight:1.6 }}>
            ⚠️ Cadastre o nome da empresa em <strong>Conta</strong>. Sem ele a loja não tem link público e o contato não aparece na vitrine.
          </div>
        )}
      </Section>
      <SalvarBloco salvando={salvando} ok={ok} erro={erro} onClick={salvar} />
      <CuponsLoja key={uid} uid={uid} />
    </div>
  );
}

/* ── Tela: Aparência ─────────────────────────────────────────── */
function TelaAparencia({ onPersonalizar, posicaoAbas, onPosicaoAbasChange, abasTravadas, onTravaAbasChange }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <ThemeAppearance />
      {posicaoAbas && onPosicaoAbasChange && <WebNavigationAppearance value={posicaoAbas} onChange={onPosicaoAbasChange} locked={abasTravadas} onLockedChange={onTravaAbasChange} />}

      {!posicaoAbas && <Section title="🎨 Personalizar App" color="var(--pc-ui-38bdf8, #38bdf8)">
        <div style={{ color:C.muted, fontSize:12, lineHeight:1.6, marginBottom:14 }}>Reordene as abas do menu conforme sua preferência.</div>
        <button onClick={onPersonalizar} style={{ width:"100%", padding:"10px", background:"rgba(56,189,248,0.08)", border:"1px solid rgba(56,189,248,0.25)", color:"var(--pc-ui-38bdf8, #38bdf8)", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>✏️ Reordenar Abas</button>
      </Section>}
    </div>
  );
}

/* ── Backup: coleções exportadas/restauradas ───────────────────── */
const BACKUP_COLECOES = ["pedidos","filamentos","impressoras","materiais","produtos","financeiro","agenda"];
const BACKUP_LABEL = { pedidos:"Pedidos", filamentos:"Filamentos", impressoras:"Impressoras", materiais:"Materiais", produtos:"Produtos", financeiro:"Financeiro", agenda:"Agenda" };

// Grava em lotes de até 400 (limite do Firestore é 500 por batch).
// Documentos com o mesmo id são sobrescritos; nada é apagado.
async function restaurarBackup(uid, data) {
  const colecoesValidas = BACKUP_COLECOES.filter(c => Array.isArray(data[c]) && data[c].length);
  if (colecoesValidas.length === 0) throw new Error("Nenhum dado reconhecível no arquivo.");

  let totalItens = 0;
  for (const col of colecoesValidas) {
    const itens = data[col].filter(it => it && it.id);
    for (let i = 0; i < itens.length; i += 400) {
      const chunk = itens.slice(i, i + 400);
      const batch = writeBatch(db);
      chunk.forEach(item => {
        const { id, ...rest } = item;
        batch.set(doc(db, `users/${uid}/${col}/${id}`), rest);
      });
      await batch.commit();
      totalItens += chunk.length;
    }
  }
  return { totalItens, colecoes: colecoesValidas };
}

/* ── Tela: Backup ────────────────────────────────────────────── */
function TelaBackup({ uid }) {
  const [exportando, setExportando] = useState(false);
  const fileRef = useRef();
  const [preview,    setPreview]    = useState(null); // { data, colecoes, totalItens }
  const [importando, setImportando] = useState(false);
  const [erroImport, setErroImport] = useState("");
  const [resultado,  setResultado]  = useState(null);

  const exportarDados = async () => {
    setExportando(true);
    try {
      const resultado = {};
      for (const nome of BACKUP_COLECOES) {
        const snap = await getDocs(collection(db, `users/${uid}/${nome}`));
        resultado[nome] = snap.docs.map(d => ({ id:d.id, ...d.data() }));
      }
      const blob = new Blob(["﻿" + JSON.stringify(resultado, null, 2)], { type:"application/json;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `printcontrol_backup_${new Date().toISOString().slice(0,10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast("Backup baixado!");
    } catch(e) { toast("Erro ao exportar: " + e.message, "error"); }
    setExportando(false);
  };

  const escolherArquivo = async (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setErroImport(""); setResultado(null); setPreview(null);
    try {
      const text = (await f.text()).replace(/^﻿/, "");
      const data = JSON.parse(text);
      const colecoesValidas = BACKUP_COLECOES.filter(c => Array.isArray(data[c]) && data[c].length);
      if (colecoesValidas.length === 0) throw new Error("Arquivo não contém dados reconhecíveis do Print Control.");
      const totalItens = colecoesValidas.reduce((s,c) => s + data[c].length, 0);
      setPreview({ data, colecoes: colecoesValidas, totalItens });
    } catch (e) {
      setErroImport(e instanceof SyntaxError ? "Arquivo inválido — selecione um backup JSON exportado pelo Print Control." : e.message);
    }
  };

  const confirmarImportacao = async () => {
    if (!preview) return;
    setImportando(true); setErroImport("");
    try {
      const r = await restaurarBackup(uid, preview.data);
      setResultado(r);
      setPreview(null);
      toast(`Backup restaurado! ${r.totalItens} registros.`);
    } catch (e) {
      setErroImport(e.message || "Erro ao importar backup.");
    }
    setImportando(false);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <Section title="💾 Baixar uma cópia" color="#a78bfa">
        <div className="settings-description">Guarde um arquivo com seus pedidos, produtos, filamentos, impressoras, materiais, financeiro e agenda.</div>
        <button onClick={exportarDados} disabled={exportando} style={{ width:"100%", padding:"10px", background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.25)", color:"#a78bfa", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", opacity:exportando?.6:1 }}>
          {exportando ? "⏳ Exportando..." : "⬇️ Baixar backup JSON"}
        </button>
      </Section>

      <Section title="☁️ Restaurar um backup" color={C.muted}>
        <div style={{ color:C.muted, fontSize:12, lineHeight:1.6, marginBottom:14 }}>
          Selecione o arquivo JSON salvo pelo Print Control. Registros com o mesmo identificador serão substituídos pelos dados do backup; os demais serão mantidos.
        </div>
        <input ref={fileRef} type="file" accept="application/json,.json" style={{ display:"none" }} onChange={escolherArquivo} />

        {!preview && (
          <button onClick={() => fileRef.current.click()} style={{ width:"100%", padding:"10px", background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.25)", color:"#a78bfa", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>
            📤 Selecionar arquivo de backup
          </button>
        )}

        {preview && (
          <div style={{ background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:10, padding:14 }}>
            <div style={{ color:C.text, fontSize:12, fontWeight:700, marginBottom:8 }}>⚠️ Confirmar restauração</div>
            <div style={{ color:C.muted, fontSize:11.5, lineHeight:1.7, marginBottom:12 }}>
              {preview.totalItens} registro{preview.totalItens!==1?"s":""} em {preview.colecoes.map(c => BACKUP_LABEL[c]||c).join(", ")}.<br/>
              Os registros correspondentes serão substituídos pelos dados deste arquivo.
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={confirmarImportacao} disabled={importando} style={{ flex:1, padding:9, background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.4)", color:"var(--pc-ui-f59e0b, #f59e0b)", borderRadius:7, fontSize:11, fontWeight:800, cursor:"pointer" }}>
                {importando ? "⏳ Restaurando..." : "Restaurar agora"}
              </button>
              <button onClick={() => setPreview(null)} disabled={importando} style={{ flex:1, padding:9, background:"var(--pc-white-4, rgba(255,255,255,0.04))", border:"1px solid "+C.border, color:C.muted, borderRadius:7, fontSize:11, cursor:"pointer" }}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        {erroImport && <div style={{ marginTop:12, background:"rgba(239,68,68,0.07)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:8, padding:"9px 12px", color:"#fca5a5", fontSize:12 }}>⚠️ {erroImport}</div>}
        {resultado && <div style={{ marginTop:12, background:"rgba(46,213,115,0.07)", border:"1px solid rgba(46,213,115,0.2)", borderRadius:8, padding:"9px 12px", color:C.success, fontSize:12 }}>✓ {resultado.totalItens} registros restaurados com sucesso!</div>}
      </Section>

      <Section title="🔄 Backup automático" color={C.muted}>
        <EmBreveRow icon="🔄" title="Ainda não disponível" subtitle="Por enquanto, use a opção de baixar uma cópia." />
      </Section>
    </div>
  );
}

/* ── Tela: Downloads ─────────────────────────────────────────── */
function TelaDownloads() {
  const wide = useIsWide();
  const mobile = !wide || window.Capacitor?.getPlatform?.() === "android";
  // O instalador Windows vem do CI; Android é distribuído pela Google Play.
  const [dl, setDl] = useState({ exeUrl: "", exeVersion: "" });

  useEffect(() => {
    getDoc(doc(db, "config", "assinatura")).then(snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      setDl({ exeUrl: d.exeUrl || "", exeVersion: d.exeVersion || "" });
    }).catch(() => {});
  }, []);

  return (
    <Section title="⬇️ Onde usar o Print Control" color="var(--pc-ui-38bdf8, #38bdf8)">
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {mobile && <LinkRow icon="🌐" title="Acessar o site Print Control" subtitle="Use também pelo navegador" href="https://printcontrol.netlify.app" />}
        {dl.exeUrl ? (
          <a href={dl.exeUrl} target="_blank" rel="noreferrer" style={{ display:"flex", alignItems:"center", gap:12, background:"rgba(56,189,248,0.06)", border:"1px solid rgba(56,189,248,0.2)", borderRadius:10, padding:"12px 14px", textDecoration:"none" }}>
            <span style={{ fontSize:22 }}>🖥️</span>
            <div style={{ flex:1 }}><div style={{ color:"var(--pc-ui-38bdf8, #38bdf8)", fontSize:12, fontWeight:800 }}>Print Control para Windows</div><div style={{ color:C.muted, fontSize:10 }}>Versão {dl.exeVersion || APP_VERSION} · Instalador .exe</div></div>
            <span style={{ color:"var(--pc-ui-38bdf8, #38bdf8)", fontSize:12 }}>⬇️</span>
          </a>
        ) : (
          <EmBreveRow icon="🖥️" title="Windows" subtitle="Download indisponível no momento" />
        )}
          <a href="https://play.google.com/store/apps/details?id=com.Otto.printcontrol" target="_blank" rel="noopener noreferrer" style={{ display:"flex", alignItems:"center", gap:12, background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.2)", borderRadius:10, padding:"12px 14px", textDecoration:"none" }}>
            <span style={{ fontSize:22 }}>📱</span>
            <div style={{ flex:1 }}>
              <div style={{ color:"var(--pc-ui-22c55e, #22c55e)", fontSize:12, fontWeight:800 }}>Print Control para Android</div>
              <div style={{ color:C.muted, fontSize:12 }}>Instalar ou atualizar pela Google Play</div>
            </div>
            <span style={{ color:"var(--pc-ui-22c55e, #22c55e)", fontSize:12 }}>⬇️</span>
          </a>
      </div>
        <div style={{ marginTop:12, fontSize:11, color:C.muted, lineHeight:1.6 }}>
          No Android, a instalação e as atualizações são feitas pela Google Play.
        </div>
    </Section>
  );
}

/* ── Tela: Sistema ───────────────────────────────────────────── */
function TelaSistema() {
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [updateInfo,     setUpdateInfo]     = useState(null);
  const [appVersion,     setAppVersion]     = useState(APP_VERSION);
  const [limpando,       setLimpando]       = useState(false);

  useEffect(() => {
    if (window.electronAPI?.getVersion) window.electronAPI.getVersion().then(v => setAppVersion(v));
  }, []);

  // Atualizações só existem no EXE (Electron) — Site e Android não checam
  // nem mostram versão (ver Section abaixo, só renderizada se isElectron).
  const isElectron = !!window.electronAPI;

  const verificarAtualizacoes = async () => {
    setCheckingUpdate(true); setUpdateInfo(null);
    try {
      const res  = await fetch("https://matriz3dsite.web.app/version.json?t=" + Date.now());
      const data = await res.json();
      const newer = versaoMaisNova(data.version, appVersion);
      setUpdateInfo({ newer, latest:data.version, current:appVersion, downloadUrl:data.downloadUrl });
    } catch { setUpdateInfo({ error:true }); }
    setCheckingUpdate(false);
  };

  const limparCache = async () => {
    setLimpando(true);
    try {
      if (window.caches?.keys) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map(k => window.caches.delete(k)));
      }
      localStorage.removeItem("notif_last_run");
      toast("Cache limpo!");
    } catch { toast("Erro ao limpar cache.", "error"); }
    setLimpando(false);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {isElectron && (
        <Section title="🔄 Atualizações" color="#a78bfa">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <span style={{ color:C.muted, fontSize:12 }}>Versão atual</span>
            <span style={{ color:C.accent2, fontWeight:800, fontSize:13, fontFamily:"monospace" }}>v{appVersion}</span>
          </div>
          <button onClick={verificarAtualizacoes} disabled={checkingUpdate} style={{ width:"100%", padding:"10px", background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.25)", color:"#a78bfa", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", opacity:checkingUpdate?.6:1 }}>
            {checkingUpdate ? "⏳ Verificando..." : "🔍 Verificar Atualizações"}
          </button>
          {updateInfo && !updateInfo.error && (
            <div style={{ marginTop:10, background:updateInfo.newer?"rgba(0,212,170,0.06)":"rgba(71,85,105,0.08)", border:"1px solid "+(updateInfo.newer?"rgba(0,212,170,0.2)":C.border), borderRadius:8, padding:"10px 12px" }}>
              {updateInfo.newer ? (
                <>
                  <div style={{ color:C.accent2, fontSize:12, fontWeight:800, marginBottom:6 }}>🚀 Nova versão disponível: v{updateInfo.latest}</div>
                  <button onClick={() => { if (window.electronAPI?.openExternal) window.electronAPI.openExternal(updateInfo.downloadUrl); else window.open(updateInfo.downloadUrl,"_blank"); }}
                    style={{ width:"100%", padding:"8px", background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", color:"var(--pc-ui-04111f, #04111f)", borderRadius:7, fontSize:11, fontWeight:800, cursor:"pointer" }}>⬇️ Baixar atualização</button>
                </>
              ) : (
                <div style={{ color:C.success, fontSize:12, fontWeight:700 }}>✓ Versão mais recente (v{updateInfo.current})</div>
              )}
            </div>
          )}
          {updateInfo?.error && <div style={{ marginTop:10, color:"#fca5a5", fontSize:11 }}>⚠️ Não foi possível verificar. Verifique sua conexão.</div>}
        </Section>
      )}

      {window.Capacitor?.getPlatform?.() === "android" && <Section title="🔔 Notificações" color={C.muted}>
        <NotificacoesCelular />
      </Section>}
      <Section title="🧹 Manutenção" color={C.muted}>
        <button onClick={limparCache} disabled={limpando} style={{ width:"100%", padding:"10px", background:"rgba(148,163,184,0.08)", border:"1px solid rgba(148,163,184,0.25)", color:C.muted, borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", marginBottom:14, opacity:limpando?.6:1 }}>
          {limpando ? "⏳ Limpando..." : "🧹 Limpar cache"}
        </button>
      </Section>
      <Section title="📄 Sobre o serviço" color={C.muted}>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <LinkRow icon="📄" title="Política de Privacidade" subtitle="Como tratamos seus dados" href="https://politica-termos.netlify.app/privacy-policy" />
          <LinkRow icon="📜" title="Termos de Uso" subtitle="Termos do serviço" href="https://politica-termos.netlify.app/terms" />
        </div>
      </Section>
    </div>
  );
}

// Liga a aba "Lojas parceiras" (consignação): users/{uid}.consignacaoAtiva
function OpcaoLojasParceiras({ perfil, uid, onUpdate }) {
  const ativo = !!perfil?.consignacaoAtiva;
  const alternar = async () => {
    try {
      await updateDoc(doc(db, "users", uid), { consignacaoAtiva: !ativo });
      onUpdate(p => ({ ...p, consignacaoAtiva: !ativo }));
    } catch { toast("Não foi possível salvar.", "error"); }
  };
  return (
    <label style={{ background:C.card, border:"1px solid "+C.border, borderRadius:12, padding:14, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:12, cursor:"pointer" }}>
      <span><strong style={{ color:C.text }}>🤝 Lojas parceiras (consignação)</strong><span style={{ display:"block", color:C.muted, fontSize:12, marginTop:4 }}>Deixe peças em lojas: elas vendem com o desconto combinado e você acompanha o repasse. Mostra a aba "Lojas parceiras".</span></span>
      <input type="checkbox" checked={ativo} onChange={alternar} style={{ width:20, height:20, accentColor:"var(--pc-accent2, #00d4aa)", flexShrink:0 }} />
    </label>
  );
}

/* ── Componente principal ───────────────────────────────────────
   Mobile: hub de categorias (cards) → tela da categoria, com
   pesquisa fixa e navegação empilhada (voltar).
   Desktop largo: lista + detalhe lado a lado, como um app de
   configurações nativo. */
function renderDetalhe(id, { perfil, uid, onUpdate, onPersonalizar, onIniciarTutorial, notificacaoDestino, posicaoAbas, onPosicaoAbasChange, abasTravadas, onTravaAbasChange }) {
  switch (id) {
    case "conta":        return <TelaConta perfil={perfil} uid={uid} onUpdate={onUpdate} focarPlano={notificacaoDestino?.secao === "conta" && notificacaoDestino?.foco === "plano" ? notificacaoDestino.recebidoEm : null} />;
    case "pagamentos":    return <TelaPagamentos uid={uid} perfil={perfil} />;
    case "vendas":        return <><OpcaoLojasParceiras perfil={perfil} uid={uid} onUpdate={onUpdate} /><TelaVendasOnline uid={uid} /></>;
    case "loja":          return <TelaLoja uid={uid} onUpdate={onUpdate} />;
    case "aparencia":     return window.Capacitor?.getPlatform?.() === "android" ? null : <TelaAparencia onPersonalizar={onPersonalizar} posicaoAbas={posicaoAbas} onPosicaoAbasChange={onPosicaoAbasChange} abasTravadas={abasTravadas} onTravaAbasChange={onTravaAbasChange} />;
    case "backup":        return <TelaBackup uid={uid} />;
    case "downloads":     return <TelaDownloads />;
    case "sistema":       return <TelaSistema />;
    case "suporte":       return <TelaSuporte key={notificacaoDestino?.recebidoEm || "suporte"} perfil={perfil} onIniciarTutorial={onIniciarTutorial} />;
    default:              return null;
  }
}

export default function Configuracoes({ perfil, uid, onUpdate, onPersonalizar, onIniciarTutorial, notificacaoDestino, posicaoAbas, onPosicaoAbasChange, abasTravadas, onTravaAbasChange }) {
  const [categoria, setCategoria] = useState(null);
  useEffect(() => {
    if (notificacaoDestino?.tab === "config" && ["downloads", "conta", "suporte", "pagamentos"].includes(notificacaoDestino.secao)) {
      setCategoria(notificacaoDestino.secao);
    }
  }, [notificacaoDestino]);
  const wide = useIsWide();

  const categoriasDisponiveis = CATEGORIAS.filter(c => c.id !== "aparencia" || window.Capacitor?.getPlatform?.() !== "android");

  const ctx = { perfil, uid, onUpdate, onPersonalizar, onIniciarTutorial, notificacaoDestino, posicaoAbas, onPosicaoAbasChange, abasTravadas, onTravaAbasChange };

  if (wide) {
    const atual = categoria || "conta";
    const catObj = CATEGORIAS.find(c => c.id === atual);
    return (
      <div className="tool-config settings-workspace settings-workspace--wide">
        <style>{PC_STYLES}</style>
        <aside className="settings-sidebar">
          <nav className="settings-nav" aria-label="Categorias de configurações">
            {categoriasDisponiveis.map(c => <CategoryRowCompact key={c.id} cat={c} ativo={atual===c.id} onClick={() => setCategoria(c.id)} />)}
          </nav>
        </aside>
        <div key={atual} className="pc-screen settings-detail">
          <DetailHeader cat={catObj} />
          {renderDetalhe(atual, ctx)}
        </div>
      </div>
    );
  }

  return (
    <div className="tool-config settings-workspace">
      <style>{PC_STYLES}</style>
      {!categoria ? (
        <div>
          <div className="settings-category-grid">{categoriasDisponiveis.map(c => <CategoryCard key={c.id} cat={c} onClick={() => setCategoria(c.id)} />)}</div>
        </div>
      ) : (
        <div key={categoria} className="pc-screen settings-detail">
          <DetailHeader cat={CATEGORIAS.find(c => c.id === categoria)} onBack={() => setCategoria(null)} />
          <div className="settings-switch"><label htmlFor="settings-category-select">Ir para</label><select id="settings-category-select" value={categoria} onChange={e => setCategoria(e.target.value)}>{categoriasDisponiveis.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</select></div>
          {renderDetalhe(categoria, ctx)}
        </div>
      )}
    </div>
  );
}
